/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState } from "react";
import SkeletonRows from "components/SkeletonRows";
import { alertService } from "services";
import { DataPage } from "types/dataResponse";
import { BASE_URL } from "utils/requests";
import Pagination from "../Pagination";
import { PencilFill, Search, TrashFill, LockFill, UnlockFill } from "react-bootstrap-icons";
import { authHeader, handleResponse } from "helpers";
import ConfirmModal from "components/ConfirmModal";

const returnUserPermission = (permission: string) => {
  if (permission === "user") {
    return "Usuário";
  } else if (permission === "admin") {
    return "Administrador";
  } else if (permission === "manager") {
    return "Gestor";
  } else {
    return "";
  }
};

const DataTable = () => {
  const [activePage, setActivePage] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [toggleRefresh, setToggleRefresh] = useState(false);
  const [page, setPage] = useState<DataPage>({
    first: true,
    last: true,
    number: 0,
    totalElements: 0,
    totalPages: 0,
  });
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState<{
    title: string;
    message: string;
    variant: "primary" | "danger" | "success";
    onConfirm: () => void;
  } | null>(null);

  const loadUsers = (activePage: number) => {
    const requestOptions = {
      method: "GET",
      headers: authHeader(),
    };

    setLoading(true);
    fetch(
      `${BASE_URL}/users?page=${activePage}&size=20&sort=name,asc`,
      requestOptions
    )
      .then(handleResponse)
      .then((response) => {
        setPage(response.data);
      })
      .finally(() => setLoading(false));
  };

  const searchUsers = (query: string, activePage: number) => {
    const requestOptions = {
      method: "GET",
      headers: authHeader(),
    };

    setLoading(true);
    fetch(
      `${BASE_URL}/users/search?query=${query}&page=${activePage}&size=20&sort=name,asc`,
      requestOptions
    )
      .then(handleResponse)
      .then((response) => {
        setPage(response.data);
      })
      .catch((error) => {
        alertService.error(error);
      })
      .finally(() => setLoading(false));
  };

  // Single-user actions ask through ConfirmModal (design system) instead of window.confirm
  const deleteUser = (name: string, email: string, id: number) => {
    setConfirm({
      title: "Desativar usuário",
      message: `Você realmente deseja desabilitar o usuário ${name} com o email ${email}?`,
      variant: "danger",
      onConfirm: () => {
        const requestOptions = {
          method: "DELETE",
          headers: authHeader(),
        };

        fetch(`${BASE_URL}/users/${id}`, requestOptions)
          .then(handleResponse)
          .then(() => loadUsers(0))
          .catch((error) => {
            alertService.error(error);
          });
      },
    });
  };

  const blockUser = (name: string, id: number) => {
    setConfirm({
      title: "Bloquear usuário",
      message: `Você realmente deseja bloquear o usuário ${name}?`,
      variant: "danger",
      onConfirm: () => {
        const requestOptions = {
          method: "PUT",
          headers: authHeader(),
        };

        fetch(`${BASE_URL}/users/${id}/block`, requestOptions)
          .then(handleResponse)
          .then(() => loadUsers(activePage))
          .catch((error) => {
            alertService.error(error);
          });
      },
    });
  };

  const unblockUser = (name: string, id: number) => {
    setConfirm({
      title: "Desbloquear usuário",
      message: `Você realmente deseja desbloquear o usuário ${name}?`,
      variant: "success",
      onConfirm: () => {
        const requestOptions = {
          method: "PUT",
          headers: authHeader(),
        };

        fetch(`${BASE_URL}/users/${id}/unblock`, requestOptions)
          .then(handleResponse)
          .then(() => loadUsers(activePage))
          .catch((error) => {
            alertService.error(error);
          });
      },
    });
  };

  const handleMassDelete = () => {
    const requestOptions = {
      method: "PUT",
      headers: {
        ...authHeader(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(selectedUsers),
    };
  
    fetch(`${BASE_URL}/users/inactive`, requestOptions)
      .then(handleResponse)
      .then((response) => {
        if (response.message) {
          alertService.success(response.message);
        }
        setSelectedUsers([]);
        setToggleRefresh(!toggleRefresh);
      })
      .catch((error) => {
        alertService.error(error);
      });
  };

  const handleMassBlock = () => {
    const requestOptions = {
      method: "PUT",
      headers: {
        ...authHeader(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(selectedUsers),
    };

    fetch(`${BASE_URL}/users/block`, requestOptions)
      .then(handleResponse)
      .then((response) => {
        if (response.message) {
          alertService.success(response.message);
        }
        setSelectedUsers([]);
        setToggleRefresh(!toggleRefresh);
      })
      .catch((error) => {
        alertService.error(error);
      });
  };

  const handleMassUnblock = () => {
    const requestOptions = {
      method: "PUT",
      headers: {
        ...authHeader(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(selectedUsers),
    };

    fetch(`${BASE_URL}/users/unblock`, requestOptions)
      .then(handleResponse)
      .then((response) => {
        if (response.message) {
          alertService.success(response.message);
        }
        setSelectedUsers([]);
        setToggleRefresh(!toggleRefresh);
      })
      .catch((error) => {
        alertService.error(error);
      });
  };

  const toggleSelectAll = () => {
    setSelectAll(!selectAll);
    if (!selectAll && Array.isArray(page.content)) {
      setSelectedUsers(page.content.map((user) => user.id));
    } else {
      setSelectedUsers([]);
    }
  };

  const toggleSelectUser = (id: number) => {
    setSelectedUsers((prev) =>
      prev.includes(id) ? prev.filter((userId) => userId !== id) : [...prev, id]
    );
  };

  useEffect(() => {
    if (isSearching) {
      searchUsers(searchTerm, activePage);
    } else {
      loadUsers(activePage);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePage, isSearching, toggleRefresh]);

  const changePage = (index: number) => {
    setActivePage(index);
    setSelectedUsers([]);
    setSelectAll(false);
  };

  const handleSearch = () => {
    if (searchTerm.trim() === "") {
      setIsSearching(false);
      setActivePage(0); 
    } else {
      setIsSearching(true);
      setActivePage(0);
    }

    setToggleRefresh(!toggleRefresh);
  };

  return (
    <>
      <div className="mt-4 mb-5">

        <h5 className="font-weight-light text-secondary">Usuários</h5>
        <div
  className="d-flex justify-content-between align-items-center flex-wrap mt-3"
  style={{ flexWrap: "nowrap" }}
>

  <div
    className="input-group"
    style={{
      width: "300px",
      position: "relative",
      flexShrink: 0,
    }}
  >
    <input
      type="text"
      className="form-control"
      placeholder="Buscar usuário"
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.target.value)}
      style={{
        borderRadius: "25px",
        borderColor: "hsl(var(--input))",
        paddingRight: "35px",
        paddingLeft: "15px",
        boxShadow: "none",
        outline: "none",
      }}
    />
    <button
      className="btn"
      onClick={handleSearch}
      style={{
        position: "absolute",
        right: "10px",
        top: "50%",
        transform: "translateY(-50%)",
        backgroundColor: "transparent",
        border: "none",
        padding: "0",
        zIndex: 10,
        outline: "none",
        cursor: "pointer",
        boxShadow: "none",
      }}
    >
      <Search
        style={{
          width: "20px",
          height: "20px",
          color: "hsl(var(--muted-foreground))",
          outline: "none",
          cursor: "pointer",
          boxShadow: "none",
        }}
      />
    </button>
  </div>

  <div className="d-flex align-items-center gap-3" style={{ marginLeft: "auto" }}>

    {selectedUsers.length > 1 && (
      <>
        <button
          className="btn btn-danger"
          onClick={() =>
            setConfirm({
              title: "Confirmação",
              message: "Tem certeza de que deseja desativar os usuários selecionados?",
              variant: "danger",
              onConfirm: handleMassDelete,
            })
          }
          style={{ marginRight: "10px" }}
        >
          Desativar
        </button>
        <button
          className="btn btn-danger"
          onClick={() =>
            setConfirm({
              title: "Confirmação de Bloqueio",
              message: "Tem certeza de que deseja bloquear os usuários selecionados? Eles não poderão mais fazer login no sistema.",
              variant: "danger",
              onConfirm: handleMassBlock,
            })
          }
          style={{ marginRight: "10px" }}
        >
          Bloquear
        </button>
        <button
          className="btn btn-success"
          onClick={() =>
            setConfirm({
              title: "Confirmação de Desbloqueio",
              message: "Tem certeza de que deseja desbloquear os usuários selecionados? Eles poderão fazer login novamente.",
              variant: "success",
              onConfirm: handleMassUnblock,
            })
          }
          style={{ marginRight: "10px" }}
        >
          Desbloquear
        </button>
      </>
    )}
    <Pagination page={page} onPageChange={changePage} />
    <a href="/users/new" className="btn btn-primary ml-3">
      Adicionar
    </a>
  </div>
</div>
        <table className="table table-hover shadow rounded bg-white text-center p-0 m-0">
          <thead>
            <tr>
            <th scope="col">
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={toggleSelectAll}
                />
              </th>
              <th scope="col">Usuário</th>
              <th scope="col">E-mail</th>
              <th scope="col">Permissão</th>
              <th scope="col">Empresa</th>
              <th scope="col">Ações</th>
            </tr>
          </thead>
          <tbody className="table-borderless">
            {loading ? <SkeletonRows cols={6} /> : Array.isArray(page.content) && page.content?.length > 0 ? ( page.content?.map((item) => (
              <tr key={item.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(item.id)}
                      onChange={() => toggleSelectUser(item.id)}
                    />
                  </td>
                <th scope="row">{item.blocked ? `${item.name} (Bloqueado)` : item.name}</th>
                <td>{item.email}</td>
                <td>{returnUserPermission(item.role)}</td>
                <td>{item.company.name}</td>
                <td>
                  <span className="table-actions">
                  <a
                    className="text-secondary"
                    href={`/users/edit/${item.id}`}
                  >
                    <PencilFill />
                  </a>
                  <a
                    className="text-secondary"
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      deleteUser(item.name, item.email, item.id);
                    }}
                  >
                    <TrashFill />
                  </a>
                  {item.role !== "admin" && (
                    <>
                      {item.blocked ? (
                        <a
                          className="text-secondary"
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            unblockUser(item.name, item.id);
                          }}
                        >
                          <UnlockFill />
                        </a>
                      ) : (
                        <a
                          className="text-secondary"
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            blockUser(item.name, item.id);
                          }}
                        >
                          <LockFill />
                        </a>
                      )}
                    </>
                  )}
                  </span>
                </td>
              </tr>
              ))
            ) : (
              <tr>
                <td >Nenhum usuário encontrado.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <ConfirmModal
        show={!!confirm}
        title={confirm?.title || ""}
        message={confirm?.message || ""}
        variant={confirm?.variant}
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          confirm?.onConfirm();
          setConfirm(null);
        }}
      />

    </>
  );
};

export default DataTable;
